import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
  HttpContext,
  HttpContextToken,
} from '@angular/common/http';

export const IS_RETRIED = new HttpContextToken<boolean>(() => false);
import {
  AUTH_LOGIN_API,
  AUTH_TOKEN_API,
  CENTRAL_PLATFORM,
  COOKIE_KEYS,
  REFRESH_TOKEN_INVALID_MSG,
  UNAUTHORIZED_OPERATION_MSG,
  HEADER_PLATFORM,
  HEADER_ROLE_ID,
  HEADER_STUDY_ID,
  SKIP_PLATFORM_HEADER,
} from '../constant/constant';
import {
  Observable,
  throwError,
  BehaviorSubject,
  type PartialObserver,
} from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { LoginService } from './login.service';
import { AuthGuardService } from './auth-guard.service';
import { Router } from '@angular/router';
import { UNABLE_TO_CONNECT_MSG } from '../constant/responseMessage';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isRefreshing: boolean = false;

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private toastr: ToastrService,
    private cookieService: CookieService,
    private loginService: LoginService,
    private authGuard: AuthGuardService,
    private router: Router,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // const studyId: string = this.cookieService.get(COOKIE_KEYS.STUDY_ID);
    const roleId: string = this.cookieService.get(COOKIE_KEYS.ROLE);

    let headers = req.headers;
    if (!req.context.get(SKIP_PLATFORM_HEADER)) {
      headers = headers.set(HEADER_PLATFORM, CENTRAL_PLATFORM);
    }
    if (roleId) headers = headers.set(HEADER_ROLE_ID, roleId);
    // if (studyId) headers = headers.set(HEADER_STUDY_ID, studyId);
    req = req.clone({ headers, withCredentials: true });

    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 0) {
            console.error('Network Error: Backend is unreachable.', error);
            this.toastr.error(UNABLE_TO_CONNECT_MSG);
            return throwError(() => error);
          }

          if (
            error.status === HttpStatusCode.Unauthorized &&
            !req.url.includes(AUTH_LOGIN_API)
          ) {
            if (req.url.includes(AUTH_TOKEN_API) || req.context.get(IS_RETRIED)) {
              console.error(REFRESH_TOKEN_INVALID_MSG);
              this.isRefreshing = false;
              this.logout();
              return throwError(() => error);
            }
            return this.handle401Error(req, next);
          }
          if (error.status === HttpStatusCode.Forbidden) {
            this.toastr.error(UNAUTHORIZED_OPERATION_MSG);
            console.error(UNAUTHORIZED_OPERATION_MSG);
            // this.router.navigate([''], { replaceUrl: true });
            return throwError(() => error);
          }
          if(error.status === HttpStatusCode.ServiceUnavailable){
            this.toastr.error(error.error?.message);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.getAccessToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          const clonedReq = request.clone({ withCredentials: true });
          clonedReq.context.set(IS_RETRIED, true);
          return next.handle(clonedReq);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          console.error('Token refresh failed:', err);
          this.logout();
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          const clonedReq = request.clone({ withCredentials: true });
          clonedReq.context.set(IS_RETRIED, true);
          return next.handle(clonedReq);
        }),
      );
    }
  }

  logout(): void {
    this.loginService.logout().subscribe((res) => {
      this.authGuard.logout();
    });
  }
}
